"use client";

import {
  Check,
  CopyX,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useBibStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trackBibliographyCreated } from "@/lib/analytics";
import {
  type Bibliography,
  createBibliography,
  deleteBibliography,
  removeDuplicateCitations,
  renameBibliography,
  setActiveBibliography,
} from "@/lib/bibliography";
import { t } from "@/lib/i18n/interpolate";
import { cn } from "@/lib/utils";

interface ProjectListProps {
  bibliographies: Bibliography[];
  activeId: string | null;
  /** Fired after any change to the project set or the active project. */
  onChanged: () => void;
  /** Called once a project has been picked, so a mobile popover can close. */
  onPicked?: () => void;
}

interface InlineNameFormProps {
  initial: string;
  placeholder: string;
  submitLabel: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}

function InlineNameForm({
  initial,
  placeholder,
  submitLabel,
  onSubmit,
  onCancel,
}: InlineNameFormProps) {
  const bib = useBibStrings();
  const [name, setName] = useState(initial);
  return (
    <form
      className="flex items-center gap-1"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim()) {
          onSubmit(name.trim());
        }
      }}
    >
      <Input
        aria-label={placeholder}
        autoFocus
        className="h-8 min-w-0 flex-1 text-sm"
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onCancel();
          }
        }}
        placeholder={placeholder}
        value={name}
      />
      <Button
        disabled={!name.trim()}
        size="icon-sm"
        title={submitLabel}
        type="submit"
        variant="ghost"
      >
        <Check className="size-4" />
        <span className="sr-only">{submitLabel}</span>
      </Button>
      <Button
        onClick={onCancel}
        size="icon-sm"
        title={bib.cancel}
        type="button"
        variant="ghost"
      >
        <X className="size-4" />
        <span className="sr-only">{bib.cancel}</span>
      </Button>
    </form>
  );
}

/**
 * The studio's project rail: every bibliography in this browser, the active one
 * highlighted, with create and rename handled inline (Linear-style) and the
 * destructive actions behind a confirmation.
 */
export function ProjectList({
  bibliographies,
  activeId,
  onChanged,
  onPicked,
}: ProjectListProps) {
  const bib = useBibStrings();
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [menuId, setMenuId] = useState<string | null>(null);
  const [dedupeTarget, setDedupeTarget] = useState<Bibliography | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Bibliography | null>(null);

  function select(id: string) {
    if (id !== activeId) {
      setActiveBibliography(id);
      onChanged();
    }
    onPicked?.();
  }

  function create(name: string) {
    createBibliography(name);
    trackBibliographyCreated();
    setCreating(false);
    onChanged();
    onPicked?.();
  }

  function rename(id: string, name: string) {
    renameBibliography(id, name);
    setRenamingId(null);
    onChanged();
  }

  function dedupe() {
    if (!dedupeTarget) {
      return;
    }
    select(dedupeTarget.id);
    const removed = removeDuplicateCitations();
    setDedupeTarget(null);
    onChanged();
    if (removed > 0) {
      toast.success(
        t(removed === 1 ? bib.duplicateRemovedOne : bib.duplicateRemovedMany, {
          count: removed,
        })
      );
    } else {
      toast.info(bib.noDuplicates);
    }
  }

  function remove() {
    if (!deleteTarget) {
      return;
    }
    deleteBibliography(deleteTarget.id);
    setDeleteTarget(null);
    onChanged();
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
          {bib.studioProjects}
        </p>
        <Button
          onClick={() => setCreating(true)}
          size="icon-xs"
          title={bib.studioNewProject}
          variant="ghost"
        >
          <FolderPlus className="size-4" />
          <span className="sr-only">{bib.studioNewProject}</span>
        </Button>
      </div>

      <ul className="flex-1 space-y-0.5 px-2">
        {bibliographies.map((project) => {
          const active = project.id === activeId;
          if (renamingId === project.id) {
            return (
              <li className="px-1 py-0.5" key={project.id}>
                <InlineNameForm
                  initial={project.name}
                  onCancel={() => setRenamingId(null)}
                  onSubmit={(name) => rename(project.id, name)}
                  placeholder={bib.newName}
                  submitLabel={bib.rename}
                />
              </li>
            );
          }
          return (
            <li
              className={cn(
                "group flex items-center gap-1 rounded-md transition-colors",
                active
                  ? "bg-background text-foreground shadow-soft"
                  : "text-muted-foreground hover:bg-background/60 hover:text-foreground"
              )}
              key={project.id}
            >
              <button
                aria-current={active ? "true" : undefined}
                className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-start text-sm"
                onClick={() => select(project.id)}
                type="button"
              >
                <span className="truncate">{project.name}</span>
                <span className="ms-auto shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[11px] tabular-nums">
                  {project.citations.length}
                </span>
              </button>
              <Popover
                onOpenChange={(open) => setMenuId(open ? project.id : null)}
                open={menuId === project.id}
              >
                <PopoverTrigger asChild>
                  <Button
                    className={cn(
                      "me-1 shrink-0",
                      active
                        ? ""
                        : "opacity-60 focus-visible:opacity-100 group-hover:opacity-100"
                    )}
                    size="icon-xs"
                    title={bib.moreOptions}
                    variant="ghost"
                  >
                    <MoreHorizontal className="size-4" />
                    <span className="sr-only">{bib.moreOptions}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-52 p-1.5">
                  <Button
                    className="w-full justify-start"
                    onClick={() => {
                      setMenuId(null);
                      setRenamingId(project.id);
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <Pencil className="me-2 size-4" />
                    {bib.rename}
                  </Button>
                  <Button
                    className="w-full justify-start"
                    disabled={project.citations.length <= 1}
                    onClick={() => {
                      setMenuId(null);
                      setDedupeTarget(project);
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <CopyX className="me-2 size-4" />
                    {bib.removeDuplicates}
                  </Button>
                  <Button
                    className="w-full justify-start text-destructive hover:text-destructive"
                    disabled={bibliographies.length <= 1}
                    onClick={() => {
                      setMenuId(null);
                      setDeleteTarget(project);
                    }}
                    size="sm"
                    title={
                      bibliographies.length <= 1
                        ? bib.cannotDeleteOnly
                        : undefined
                    }
                    variant="ghost"
                  >
                    <Trash2 className="me-2 size-4" />
                    {bib.delete}
                  </Button>
                </PopoverContent>
              </Popover>
            </li>
          );
        })}
        {creating ? (
          <li className="px-1 py-0.5">
            <InlineNameForm
              initial=""
              onCancel={() => setCreating(false)}
              onSubmit={create}
              placeholder={bib.bibliographyName}
              submitLabel={bib.create}
            />
          </li>
        ) : (
          <li>
            <button
              className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-muted-foreground text-sm transition-colors hover:bg-background/60 hover:text-foreground"
              onClick={() => setCreating(true)}
              type="button"
            >
              <FolderPlus className="size-4" />
              {bib.studioNewProject}
            </button>
          </li>
        )}
      </ul>

      <Dialog
        onOpenChange={(open) => !open && setDedupeTarget(null)}
        open={!!dedupeTarget}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{bib.removeDuplicatesConfirm}</DialogTitle>
            <DialogDescription>
              {t(bib.removeDuplicatesBody, { name: dedupeTarget?.name ?? "" })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setDedupeTarget(null)}
              type="button"
              variant="outline"
            >
              {bib.cancel}
            </Button>
            <Button onClick={dedupe}>{bib.removeDuplicates}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        open={!!deleteTarget}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{bib.deleteBibliographyConfirm}</DialogTitle>
            <DialogDescription>
              {t(bib.deleteBibliographyBody, {
                name: deleteTarget?.name ?? "",
                count: deleteTarget?.citations.length ?? 0,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setDeleteTarget(null)}
              type="button"
              variant="outline"
            >
              {bib.cancel}
            </Button>
            <Button onClick={remove} variant="destructive">
              {bib.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
